import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  of,
  catchError,
  throwError,
  Subject,
  map,
  tap,
  finalize
} from 'rxjs';
import { retry, delay } from 'rxjs/operators';
import { ControllAppService } from './controllApp.service';
import { LoginRequestDto } from '../models/autenticar.request.dto';
import { AuthUserResponseDto } from '../models/autenticar.response.dto';
import { environment } from '../../environments/environment';
import Swal from 'sweetalert2/dist/sweetalert2.js';


@Injectable({
  providedIn: 'root',
})
export class AuthService {
  autenticado = false;
  // When true, the session is considered invalid and other services should stop making requests
  public sessionInvalidated: boolean = false;
  usuarioId = '';
  nomeUsuario: string = '';
  userName: string = '';
  role: string = '';
  nomeDaEmpresa: string = '';

  private autenticadoSubject = new BehaviorSubject<boolean>(false);
  public autenticado$ = this.autenticadoSubject.asObservable();
  private logoutSubject = new Subject<void>();
  private errorMessageSubject = new Subject<string>();
  public errorMessage$ = this.errorMessageSubject.asObservable();
  private userRoleSubject = new BehaviorSubject<string | null>(null);
  private tokenValidationIntervalId: any = null;

  constructor(
    private http: HttpClient,
    private router: Router,
  private controllAppService: ControllAppService,
  
  ) {
    this.restaurarEstadoDoLocalStorage();
    // Listen for session invalidation from other tabs/windows
    try {
      window.addEventListener('storage', (ev: StorageEvent) => {
        if (ev.key === 'sessionInvalidated' && ev.newValue === '1') {
          try {
            this.sessionInvalidated = true;
            this.stopTokenValidationPolling();
          } catch (e) {}
          // Show modal in this tab as well
          this.showSessionExpiredModal();
        }
      });
    } catch (e) {}
  }

  private showSessionExpiredModal(): void {
    try {
      const globalKey = '__sessionExpiredModalOpen';
      if ((window as any)[globalKey]) return;
      (window as any)[globalKey] = true;

      Swal.fire({
        title: 'Sessão Expirada',
        text: 'Sua conta foi acessada em outro dispositivo. Por favor, faça login novamente.',
        icon: 'warning',
        confirmButtonText: 'OK',
        allowOutsideClick: () => false,
        allowEscapeKey: false,
        allowEnterKey: false,
        willOpen: () => {
          try {
            const container = document.querySelector('.swal2-container') as HTMLElement | null;
            const popup = document.querySelector('.swal2-popup') as HTMLElement | null;
            if (container) container.style.setProperty('z-index', '2147483647', 'important');
            if (popup) popup.style.setProperty('z-index', '2147483648', 'important');
          } catch (e) {}
        }
      }).then(() => {
        (window as any)[globalKey] = false;
        this.limparDadosLocais();
      }).catch(() => {
        (window as any)[globalKey] = false;
        this.limparDadosLocais();
      });
    } catch (e) {
      try { this.limparDadosLocais(); } catch (err) {}
    }
  }

  public isLoggedIn(): boolean {
    return this.autenticadoSubject.getValue();
  }

  public getAutenticadoStatus(): Observable<boolean> {
    return this.autenticado$;
  }

  public getUsuario(): any {
    const usuarioStorage = localStorage.getItem('usuario');
    return usuarioStorage ? JSON.parse(usuarioStorage).usuario : null;
  }

  public login(userName: string, senha: string): void {
    // Directly proceed with authentication without geolocation
  this.autenticarPrimeiraTentativa(userName, senha);
  }

  public logout(): void {
    // Busca o usuarioId e token
    const token = this.getToken();
    let usuarioId = null;
    
    // Tenta obter o usuarioId do objeto usuario no localStorage
    const usuario = this.getUsuario();
    if (usuario?.usuarioId) {
      usuarioId = usuario.usuarioId;
    } else {
      // Se não encontrar no objeto usuario, tenta buscar direto do localStorage
      const usuarioDataString = localStorage.getItem('usuario');
      if (usuarioDataString) {
        try {
          const parsedUsuarioData = JSON.parse(usuarioDataString);
          usuarioId = parsedUsuarioData.usuarioId;
        } catch (e) {
          console.error('Erro ao analisar dados do usuário:', e);
        }
      }
    }
    
    // Verifica se temos as informações necessárias para fazer o logout no servidor
    if (usuarioId && token) {
      
      // Prepara o corpo da requisição exatamente como o Swagger espera
      const logoutData = {
        usuarioId: usuarioId,
        token: token
      };

      // Configura os headers corretamente para corresponder ao Swagger
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`  // Adiciona o token no formato esperado pelo servidor
      });

      // Faz a requisição com os headers corretos
      this.http.post(`${environment.controllApp}/usuario/logout`, logoutData, { headers })
        .pipe(
          // Garante que as ações locais sejam executadas mesmo se a chamada falhar
          finalize(() => {
            this.limparDadosLocais();
          })
        )
        .subscribe({
          next: (response) => {
            console.log('Logout realizado com sucesso no servidor. Token invalidado e status atualizado.');
          },
          error: (err) => {
            console.error('Erro ao realizar logout no servidor:', err);
            // A limpeza local ainda será realizada pelo finalize()
          }
        });
      // Ensure token validation polling is stopped
      this.stopTokenValidationPolling();
    } else {
      console.warn('UsuarioId ou token não disponível. Realizando apenas logout local.');
      this.limparDadosLocais();
    }
  }
  
  private limparDadosLocais(): void {
  // Limpa localStorage e sessionStorage
  this.clearAllStorageData();
    
  // Reseta variáveis do serviço
  this.autenticado = false;
  this.usuarioId = '';
  this.nomeUsuario = '';
  this.userName = '';
  this.role = '';
  this.nomeDaEmpresa = '';
    
  // Notifica observadores
  this.autenticadoSubject.next(false);
  this.logoutSubject.next();
    
  // Redireciona para a página de login
  this.router.navigate(['/pages/usuarios/autenticar'], { replaceUrl: true });
  }

  public onLogout(): Observable<void> {
    return this.logoutSubject.asObservable();
  }

  private autenticarPrimeiraTentativa(
    userName: string,
    senha: string
  ): void {
    // Preparar dados para envio sem localização
    const dadosAutenticacao: LoginRequestDto = {
      userName,
      senha,
      localizacoes: [] // Remove location data as it's no longer required
    };

    // Primeira tentativa: controllApp
    this.http
      .post<AuthUserResponseDto>(`${environment.controllApp}/usuario/authenticate`, dadosAutenticacao)
      .pipe(
        retry({
          count: 3,
          delay: (error, retryCount) => {
            if (error.status === 500) {
              this.errorMessageSubject.next(`Tentando reconectar... (${retryCount}/3)`);
              return of({}).pipe(delay(2000)); // 2 segundos entre tentativas
            }
            return throwError(() => error);
          }
        }),
        map(response => {
          if (!response || !response.token) {
            throw new Error('Resposta inválida do servidor');
          }
          return response;
        }),
        catchError((error) => {
          let errorMessage = 'Erro ao realizar login';

          // Log detalhado do erro
          console.error('Erro detalhado:', {
            status: error.status,
            message: error.error?.message,
            detail: error.error?.detail
          });

          if (error.status === 500) {
            // Verifica se é erro específico de tabela não encontrada
            if (error.error?.detail?.includes('USER_TOKENS')) {
              errorMessage = 'Erro de configuração no servidor. Entre em contato com o suporte.';
              console.error('Erro de banco de dados - Tabela USER_TOKENS não encontrada');
            } else {
              errorMessage = 'Erro interno do servidor. Por favor, tente novamente mais tarde.';
            }
          } else if (error.status === 401) {
            errorMessage = 'Usuário ou senha inválidos';
          } else if (error.status === 403) {
            errorMessage = 'Acesso não autorizado';
          } else if (error.status === 0) {
            errorMessage = 'Verifique sua conexão com a internet';
          } else {
            errorMessage = 'Ocorreu um erro inesperado. Tente novamente.';
          }

          this.errorMessageSubject.next(errorMessage);
          return throwError(() => ({
            message: errorMessage,
            originalError: error,
            isDataBaseError: error.error?.detail?.includes('USER_TOKENS')
          }));
        })
      )
      .subscribe({
        next: (response: AuthUserResponseDto) => {
          this.processLoginSuccess(response);
          this.autenticadoSubject.next(true);
          this.errorMessageSubject.next(''); // Clear any previous error
        },
        error: (error) => {
          console.error('Falha na autenticação:', error);
          this.autenticadoSubject.next(false);
          // Error message already handled in catchError
        },
      });
  }

  private processLoginSuccess(response: AuthUserResponseDto): void {
    if (response.token) {
      localStorage.setItem('token', response.token);
    }

    const usuarioData = this.montarUsuarioData(response); // Sem latitude/longitude
    if (!usuarioData.usuarioId) {
      console.error('usuarioId não encontrado na resposta');
      return;
    }

    localStorage.setItem('usuario', JSON.stringify(usuarioData));
    localStorage.setItem('usuarioId', usuarioData.usuarioId);

    try {
      if (usuarioData && usuarioData.role) {
        this.userRoleSubject.next(usuarioData.role);
        this.role = usuarioData.role;
      }
    } catch (e) {
      // ignore
    }

    // Redireciona com base na role
    const role = usuarioData.role?.toLowerCase();
    let rotaDestino = '/admin/dashboard';
    if (role === 'colaborador') {
      rotaDestino = `/colaborador/formulario-recadastramento/`;
    }

    this.router.navigate([rotaDestino], { replaceUrl: true });

    // Start periodic token validation (1 minute)
    this.startTokenValidationPolling();
  }

  private startTokenValidationPolling(): void {
    // Clear existing if any
    if (this.tokenValidationIntervalId) clearInterval(this.tokenValidationIntervalId);

    const checkNow = async () => {
      const token = this.getToken();
      if (!token) return;

      const usuarioId = this.getUsuario()?.usuarioId || JSON.parse(localStorage.getItem('usuario') || '{}')?.usuarioId;

      try {
        const resp = await fetch(`${environment.controllApp}/usuario/validate-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ token, userId: usuarioId })
        });
        if (!resp.ok) {
          console.warn('[AuthService] validate-token retornou status não OK:', resp.status);
          // If server explicitly returns 440, treat as invalid token and show modal immediately
          if (resp.status === 440) {
            // Immediately stop token polling and any syncs/dispatches to prevent further requests
            try { this.stopTokenValidationPolling(); } catch (e) {}
            try { this.sessionInvalidated = true; } catch (e) {}
            try { localStorage.setItem('sessionInvalidated', '1'); } catch (e) {}
            try { localStorage.removeItem('token'); } catch (e) {}
            try { localStorage.removeItem('usuarioLogadoSync'); } catch (e) {}
            try { localStorage.removeItem('usuarioIdAtivo'); } catch (e) {}
            try { localStorage.removeItem('ultimoEnvioTentado'); } catch (e) {}
            try { localStorage.removeItem('pendingLocalizacoes'); } catch (e) {}

            // use helper to show modal and cleanup
            this.showSessionExpiredModal();
          }
          return;
        }

        const data = await resp.json();
        const ok = data === true || data === 'true';

        if (!ok) {
          // Immediately stop polling and any syncs/dispatches to prevent further requests
          try { this.stopTokenValidationPolling(); } catch (e) {}
          try { this.sessionInvalidated = true; } catch (e) {}
          try { localStorage.setItem('sessionInvalidated', '1'); } catch (e) {}
          try { localStorage.removeItem('token'); } catch (e) {}
          try { localStorage.removeItem('usuarioLogadoSync'); } catch (e) {}
          try { localStorage.removeItem('usuarioIdAtivo'); } catch (e) {}
          try { localStorage.removeItem('ultimoEnvioTentado'); } catch (e) {}
          try { localStorage.removeItem('pendingLocalizacoes'); } catch (e) {}

          // central modal + cleanup
          this.showSessionExpiredModal();
        }
      } catch (err) {
        console.warn('[AuthService] Erro de rede ao validar token:', err);
      }
    };

    // Run first check immediately
    checkNow();

    this.tokenValidationIntervalId = setInterval(() => { checkNow(); }, 60 * 1000);
  }

  private stopTokenValidationPolling(): void {
    if (this.tokenValidationIntervalId) {
      clearInterval(this.tokenValidationIntervalId);
      this.tokenValidationIntervalId = null;
    }
  }

  private montarUsuarioData(response: AuthUserResponseDto): any {
    return {
      usuario: response,
      usuarioId: response.usuarioId,
      nomeUsuario: response.userName,
      token: response.token,
      role: response.role,
      isOnline: true,
      ultimaAutenticacao: response.dataHoraAutenticacao || new Date().toISOString(),
    };
  }

  public getToken(): string | null {
    return localStorage.getItem('token');
  }

  public setToken(token: string): void {
    localStorage.setItem('token', token);
  }

  public verificarToken(): Observable<any> {
    const token = this.getToken();
    if (!token) {
      return throwError(() => new Error('Token não encontrado'));
    }
    return of(token);
  }


  private clearAllStorageData(): void {
    localStorage.clear();
    sessionStorage.clear();
  }

  private restaurarEstadoDoLocalStorage(): void {
    const usuario = this.getUsuario();
    if (usuario?.usuarioId) {
      this.autenticado = true;
      this.nomeUsuario = usuario.nome;
      this.role = usuario.role;
      this.usuarioId = usuario.usuarioId || '';

      this.autenticadoSubject.next(true);
      this.startTokenValidationPolling();
    }
  }

  public getErrorMessage(): Observable<string> {
    return this.errorMessage$;
  }

  private loadUserDataFromStorage() {
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const user = JSON.parse(userData);
        this.userRoleSubject.next(user.role);
      }
    } catch (error) {
      console.error('Erro ao carregar dados do usuário do localStorage:', error);
    }
  }
  
  getUserRoleSync(): string | null {
    // Return the subject value if available
    if (this.userRoleSubject && this.userRoleSubject.value) return this.userRoleSubject.value;

    // Fallback to the in-memory role set during restore/login
    if (this.role) return this.role;

    // As a last resort, try to read the stored 'usuario' object from localStorage
    try {
      const stored = localStorage.getItem('usuario');
      if (stored) {
        const parsed = JSON.parse(stored);
        const r = parsed?.role || parsed?.usuario?.role || null;
        if (r) {
          // populate the subject for future fast reads
          try { this.userRoleSubject.next(r); } catch (e) {}
          this.role = r;
          return r;
        }
      }
    } catch (e) {
      // ignore parse errors
    }

    return null;
  }
  
  isSuperAdmin(): boolean {
    return this.getUserRoleSync() === 'SuperAdministrador';
  }
  
  canPerformOperation(operation: string): boolean {
    switch (operation) {
      case 'deleteUser':
      case 'registerUser':
      case 'updateUserRole':
        return this.isSuperAdmin();
      default:
        return true;
    }
  }
}